import { ProposalDAOInterface, ProposalDataDAOInterface } from '../ProposalDAO';
import { BaseProposalDAO } from '../ProposalDAOImpl';
import AssetAllocationDAO from './AssetAllocationDAO';
import * as ProposalNS from '../../Proposal';
import * as ProposalElementNS from '../../ProposalElements';
import { getPortfoliosForView, createAggregatedPortfolio } from '../../utils/client-level-summary';
import { CLIENT_LEVEL_VIEW } from '@/enums/ClientLevelView';
import DonutData from '@/types/donut-data';
import SeriesData from '@/types/series-data';
import LineSeriesData from '@/types/line-series-data';
import ScatterPlotData from '@/types/scatter-plot-data';

export default class AggregatedAssetAllocationDAO extends BaseProposalDAO implements ProposalDataDAOInterface {
  private assetAllocationDAO: AssetAllocationDAO | null = null;

  private view: CLIENT_LEVEL_VIEW = CLIENT_LEVEL_VIEW.CURRENT;

  public static buildInstance(proposal: ProposalNS.Proposal, proposalElement: ProposalElementNS.ProposalDataElement): ProposalDAOInterface {
    return new AggregatedAssetAllocationDAO(proposal, proposalElement);
  }

  protected build(): void {
    const daoConfig: ProposalNS.AggregatedAssetAllocationConfig = this.proposalElement.DAO as ProposalNS.AggregatedAssetAllocationConfig;

    // Extract view configuration
    this.view = daoConfig.view;

    // Step 1: Get portfolios for the view
    const portfolios = getPortfoliosForView(this.proposal, this.view);

    if (portfolios.length === 0) {
      return;
    }

    // Step 2: Create aggregated portfolio
    const aggregatedPortfolio = createAggregatedPortfolio(portfolios, this.view);

    // Step 3: Create modified proposal with aggregated portfolio
    const aggregatedProposal: ProposalNS.Proposal = {
      ...this.proposal,
      items: [aggregatedPortfolio],
    };

    // Step 4: Create AssetAllocationDAO with aggregated proposal
    // Pass through all AssetAllocationDAO config (grouping, extras, etc.)
    const assetAllocationConfig: ProposalNS.AssetAllocationConfig = {
      type: ProposalNS.DAOType.AssetAllocation,
      groupByAllocationsDAOOptions: daoConfig.groupByAllocationsDAOOptions,
      extraColumnsDAOOptions: daoConfig.extraColumnsDAOOptions,
      showRowHeaderColors: daoConfig.showRowHeaderColors,
      labelOverride: daoConfig.labelOverride,
    };

    const assetAllocationElement: ProposalElementNS.ProposalDataElement = {
      ...this.proposalElement,
      DAO: assetAllocationConfig,
    };

    this.assetAllocationDAO = AssetAllocationDAO.buildInstance(aggregatedProposal, assetAllocationElement) as AssetAllocationDAO;
    this.assetAllocationDAO.buildDAO();
  }

  // eslint-disable-next-line class-methods-use-this
  protected postBuild(): void {
    // No post-build logic needed - AssetAllocationDAO handles everything
  }

  /**
   * Get donut chart data for aggregated portfolios
   * Delegates to AssetAllocationDAO which handles all filtering, grouping, and formatting
   */
  public getDonutData(index: number = 0): DonutData | null {
    if (!this.assetAllocationDAO) {
      return null;
    }

    return this.assetAllocationDAO.getDonutData(index);
  }

  /**
   * Get the underlying AssetAllocationDAO for advanced usage
   */
  public getAssetAllocationDAO(): AssetAllocationDAO | null {
    return this.assetAllocationDAO;
  }

  // eslint-disable-next-line class-methods-use-this
  protected getColorKeys(): string[] {
    return [];
  }

  // eslint-disable-next-line class-methods-use-this
  public getSeriesData(): SeriesData[][] {
    // Not used for donut charts
    return [];
  }

  // eslint-disable-next-line class-methods-use-this
  public getGraphConfigOptions(): ProposalNS.ChartConfigOptions {
    // Not used for donut charts
    return {};
  }

  // eslint-disable-next-line class-methods-use-this
  public getLineSeries(): LineSeriesData[] {
    // Not used for donut charts
    return [];
  }

  // eslint-disable-next-line class-methods-use-this
  public getScatterSeries(): ScatterPlotData[] {
    // Not used for donut charts
    return [];
  }
}
